import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    startWith,
    switchMap,
    take,
    takeUntil,
    withLatestFrom
} from 'rxjs/operators';

import {
    AUTOCOMPLETE_DEBOUNCE_TIME,
    BaseNestedFieldDefinition,
    BatchFieldReadHelper,
    DottedKey,
    ExtendedBaseProfileField,
    FieldReadHelper,
    FormSelectOption,
    FormValue,
    FormValueState,
    isNotNullOrUndefined,
    MultiFormSelectOptions,
    PartialRecord,
    ShComplexInputFieldComponent,
    ShFieldDefinitionService
} from '@stylehaven/components';
import { isEqual } from 'lodash-es';
import { WebserviceData } from '../../../../external-webservice/entities/webservice-data.entity';
import { JdnWebserviceConfigJson } from './jdn-webservice-config.entity';
import { JdnWebservice } from './jdn-webservice.service';

@Component({
    standalone: false,
    selector: 'med-jdn-input-field',
    templateUrl: './jdn-input-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class JdnInputFieldComponent extends ShComplexInputFieldComponent {
    public webserviceOptions$: Observable<MultiFormSelectOptions>;

    private readonly searchTerm$: Subject<string> = new Subject<string>();
    private selectedProjectOption$: Subject<FormSelectOption<string> | null> =
        new Subject<FormSelectOption<string> | null>();

    private projectIdFieldDefinition$: Observable<BaseNestedFieldDefinition>;

    public id: string = 'JDNProjectControl';
    public jdnProjectControl: UntypedFormControl = new UntypedFormControl();

    private parentForm: UntypedFormGroup;
    private initiatedControls = false;
    private projectFieldDottedKeys: string[] = [
        'Dynamic.JDNProjectId',
        'Dynamic.JDNProjectName',
        'Dynamic.JDNProfileCode',
        'Descriptive.Description',
        'Dynamic.JDNProjectPeriod',
        'Dynamic.JDNRegions',
        'Dynamic.JDNCountries',
        'Dynamic.JDNCity',
        'Dynamic.JDNListActivityDredgingAndMarineWorks',
        'Dynamic.JDNListActivityOffshoreServices',
        'Dynamic.JDNListActivityCivilWorks',
        'Dynamic.JDNListActivityEnvironmentalWorks',
        'Dynamic.JDNListActivityBrownFieldDevelopment',
        'Dynamic.JDNListActivitySoiltech',
        'Dynamic.JDNListActivityProjectDevAndConceptDesign',
        'Dynamic.JDNProjectSynced'
    ];

    constructor(
        protected fieldHelper: FieldReadHelper,
        private fieldDefinitionService: ShFieldDefinitionService,
        private webservice: JdnWebservice,
        @Inject('config') config: JdnWebserviceConfigJson
    ) {
        super(fieldHelper);

        this.projectIdFieldDefinition$ = this.fieldDefinitionService
            .getByDottedKey('Dynamic.JDNProjectId' as DottedKey)
            .pipe(
                filter((fieldDef): fieldDef is BaseNestedFieldDefinition => {
                    if (!fieldDef) {
                        console.error('Dynamic.JDNProjectId not found');
                        return false;
                    }
                    return true;
                })
            );

        this.webservice.initWebservice(config);
    }

    protected init(
        profileField: ExtendedBaseProfileField,
        fragments: PartialRecord[],
        control: UntypedFormControl
    ): void {
        this.parentForm = control.parent as UntypedFormGroup;

        if (control.disabled) {
            this.jdnProjectControl.disable();
        }

        this.projectIdFieldDefinition$
            .pipe(
                switchMap(projectIdFieldDefinition =>
                    this.initInitialProjectValue(projectIdFieldDefinition, fragments)
                ),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(initialValue => {
                if (initialValue) {
                    this.selectedProjectOption$.next(initialValue);
                    this.jdnProjectControl.setValue([new FormValue(initialValue?.value)]);
                }
            });

        this.webserviceOptions$ = this.searchTerm$.pipe(
            distinctUntilChanged(),
            debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
            startWith(''),
            withLatestFrom(this.selectedProjectOption$.pipe(startWith(null))),
            switchMap(([searchTerm, selectedOption]) =>
                this.addSelectedProjectToOptions(this.webservice.globalSearch(searchTerm), selectedOption)
            )
        );
        this.initJdnProjectControlListener();
    }

    private addSelectedProjectToOptions(
        filteredOptions$: Observable<MultiFormSelectOptions>,
        selectedOption: FormSelectOption<string> | null
    ): Observable<MultiFormSelectOptions> {
        return filteredOptions$.pipe(
            map(filteredOptions => ({
                isLiveData: filteredOptions.isLiveData,
                options:
                    !!selectedOption && this.checkSelectedNotInOptions(selectedOption, filteredOptions.options)
                        ? [...filteredOptions.options, selectedOption]
                        : filteredOptions.options
            }))
        );
    }

    private checkSelectedNotInOptions(selectedOption: FormSelectOption<string>, options: FormSelectOption[]): boolean {
        return !options.find(option => option.value === selectedOption.value);
    }

    private initInitialProjectValue(
        projectIdFieldDefinition: BaseNestedFieldDefinition,
        fragments: PartialRecord[]
    ): Observable<FormSelectOption<string> | null> {
        if (!projectIdFieldDefinition) {
            console.error(`JdnInputFieldComponent:: Could not find FieldDefinition for JDNProjectId`);
            return of(null);
        }

        const projectIdValue: FormValue<string | null> = this.isBatchEdit
            ? (BatchFieldReadHelper.getBatchFieldValue(projectIdFieldDefinition) as FormValue<string | null>)
            : this.fieldHelper.getMergedSimpleFieldValue(projectIdFieldDefinition, fragments);

        return projectIdValue.state === FormValueState.SET && projectIdValue.value
            ? this.webservice.getOptionById(projectIdValue.value).pipe(take(1))
            : of(null);
    }

    private initJdnProjectControlListener(): void {
        this.initSetProjectListener();
        this.initUnsetProjectListener();
    }

    private setProject(project: WebserviceData): void {
        this.initProjectFormControls();

        this.updateProjectFormControls(project);
        this.selectedProjectOption$.next({
            label: project.projectName as string,
            value: project.projectId as string
        });
    }

    private initSetProjectListener(): void {
        this.jdnProjectControl.valueChanges
            .pipe(
                distinctUntilChanged(isEqual),
                map((formValues: FormValue<string>[]) =>
                    formValues.filter(formValue => formValue.state === FormValueState.SET)
                ),
                filter(formValues => formValues.length > 0),
                switchMap(formValue => this.webservice.getById(formValue[0].value)),
                takeUntil(this.destroy$)
            )
            .subscribe(values => this.setProject(values));
    }

    private initUnsetProjectListener(): void {
        this.jdnProjectControl.valueChanges
            .pipe(
                isNotNullOrUndefined(),
                distinctUntilChanged(isEqual),
                withLatestFrom(this.selectedProjectOption$),
                map(([formValues, currentValue]) =>
                    formValues.filter(
                        (formValue: FormValue<string>) =>
                            formValue.state === FormValueState.UNSET && currentValue?.value === formValue.value
                    )
                ),
                filter(formValues => formValues.length > 0),
                switchMap(formValue => this.webservice.getById(formValue[0].value)),
                takeUntil(this.destroy$)
            )
            .subscribe(values => this.unsetProject());
    }

    private unsetProject(): void {
        this.initProjectFormControls();

        this.updateProjectFormControls({});
        this.selectedProjectOption$.next(null);
    }

    /**
     *   This adds the project fields to the parent FormGroup.
     *   This FormGroup is in turn used to construct the data to write to the record.
     *   The initiatedControls boolean ensures that this loop will only run once.
     */
    private initProjectFormControls(): void {
        if (this.initiatedControls) {
            return;
        }

        this.projectFieldDottedKeys.forEach(key => {
            if (!this.getFormControl(key)) {
                this.parentForm.addControl(key, new UntypedFormControl());
            }
        });

        this.initiatedControls = true;
    }

    private getFormControl(dottedKey: string): UntypedFormControl | null {
        return (this.parentForm.controls[dottedKey] as UntypedFormControl) ?? null;
    }

    private updateProjectFormControls(project: WebserviceData): void {
        this.getFormControl('Dynamic.JDNProjectSynced')?.setValue(new FormValue(true));

        this.updateSimpleFormControl('Dynamic.JDNProjectId', 'projectId', project);
        this.updateSimpleFormControl('Dynamic.JDNProjectName', 'projectName', project);
        this.updateSimpleFormControl('Dynamic.JDNProfileCode', 'code', project);

        this.updateSimpleFormControl('Descriptive.Description', 'projectDescription', project);
        this.updateSimpleFormControl('Dynamic.JDNProjectPeriod', 'period', project);

        this.updateListFormControl('Dynamic.JDNRegions', 'region', project);
        this.updateListFormControl('Dynamic.JDNCountries', 'country', project);
        this.updateSimpleFormControl('Dynamic.JDNCity', 'location', project);

        this.updateActivityFormControl(
            'Dynamic.JDNListActivityDredgingAndMarineWorks',
            'ActivityDredgingAndMarineWorks',
            project
        );
        this.updateActivityFormControl('Dynamic.JDNListActivityOffshoreServices', 'ActivityOffshoreServices', project);
        this.updateActivityFormControl('Dynamic.JDNListActivityCivilWorks', 'ActivityCivilWorks', project);
        this.updateActivityFormControl(
            'Dynamic.JDNListActivityEnvironmentalWorks',
            'ActivityEnvironmentalWorks',
            project
        );
        this.updateActivityFormControl(
            'Dynamic.JDNListActivityBrownFieldDevelopment',
            'ActivityBrownFieldDevelopment',
            project
        );
        this.updateActivityFormControl('Dynamic.JDNListActivitySoiltech', 'ActivitySoiltech', project);
        this.updateActivityFormControl(
            'Dynamic.JDNListActivityProjectDevAndConceptDesign',
            'ActivityProjectDevAndConceptDesign',
            project
        );
    }

    private updateSimpleFormControl(dottedKey: string, projectKey: string, project: WebserviceData): void {
        if (!project[projectKey]) {
            this.getFormControl(dottedKey)?.setValue(new FormValue(null));
            return;
        }

        this.getFormControl(dottedKey)?.setValue(new FormValue(project[projectKey].toString() ?? null));
    }

    private updateListFormControl(dottedKey: string, projectKey: string, project: WebserviceData): void {
        const oldFormValues: FormValue<string | null>[] = this.getFormControl(dottedKey)?.value.map(
            (formValue: FormValue<string>) => new FormValue(formValue.value, FormValueState.UNSET)
        );
        let newFormValues: FormValue<string | null>[];

        if (!project[projectKey]) {
            newFormValues = [new FormValue(null)];
        } else {
            newFormValues = [new FormValue(project[projectKey].toString())];
        }

        this.getFormControl(dottedKey)?.setValue([...oldFormValues, ...newFormValues]);
    }

    private updateActivityFormControl(dottedKey: string, projectKey: string, project: WebserviceData): void {
        const oldFormValues: FormValue<string | null>[] = this.getFormControl(dottedKey)?.value.map(
            (formValue: FormValue<string>) => new FormValue(formValue.value, FormValueState.UNSET)
        );
        let newFormValues: FormValue<string | null>[];

        if (!project[projectKey]) {
            newFormValues = [new FormValue(null)];
        } else {
            const activities: string[] = (project[projectKey] ?? []) as unknown as string[];
            newFormValues = activities.map(activity => new FormValue(activity));
        }

        this.getFormControl(dottedKey)?.setValue([...oldFormValues, ...newFormValues]);
    }

    public onSearchTermChange(term: string): void {
        this.searchTerm$.next(term);
    }
}
